import React from 'react';
import { Text, TouchableOpacity, View } from 'react-native';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import DLOTooltip from '../../../../../modules/dlo/components/DLOTooltip';
import KPIFilterDropdown, { KPIFilterDropdownItem } from '../../../../AnalyticsHub/components/KPIFilterDropdown';
import styles from '../OrderRulesReport.styles';

export type ExtendedFilterKey =
  | 'region'
  | 'market'
  | 'location'
  | 'customerBp'
  | 'customer'
  | 'keyAccount'
  | 'package'
  | 'materialNumber'
  | 'material'
  | 'year'
  | 'period'
  | 'week';

export type QuickTimeFrameKey = 'wtd' | 'ptd' | 'qtd' | 'ytd' | 'lcw' | 'lcp';

interface OrderRulesFilterBarProps {
  openFilterKey: ExtendedFilterKey | null;
  setOpenFilterKey: (key: ExtendedFilterKey | null) => void;
  regionOptions: KPIFilterDropdownItem[];
  selectedRegions: KPIFilterDropdownItem[];
  onRegionChange: (value: KPIFilterDropdownItem[]) => void;
  marketOptions: KPIFilterDropdownItem[];
  selectedMarkets: KPIFilterDropdownItem[];
  onMarketChange: (value: KPIFilterDropdownItem[]) => void;
  locationOptions: KPIFilterDropdownItem[];
  selectedLocations: KPIFilterDropdownItem[];
  onLocationChange: (value: KPIFilterDropdownItem[]) => void;
  customerBpOptions: KPIFilterDropdownItem[];
  selectedCustomerBps: KPIFilterDropdownItem[];
  onCustomerBpChange: (value: KPIFilterDropdownItem[]) => void;
  onCustomerBpSearchChange?: (text: string) => void;
  customerOptions: KPIFilterDropdownItem[];
  selectedCustomers: KPIFilterDropdownItem[];
  onCustomerChange: (value: KPIFilterDropdownItem[]) => void;
  onCustomerSearchChange?: (text: string) => void;
  keyAccountOptions: KPIFilterDropdownItem[];
  selectedKeyAccounts: KPIFilterDropdownItem[];
  onKeyAccountChange: (value: KPIFilterDropdownItem[]) => void;
  onKeyAccountSearchChange?: (text: string) => void;
  packageOptions: KPIFilterDropdownItem[];
  selectedPackages: KPIFilterDropdownItem[];
  onPackageChange: (value: KPIFilterDropdownItem[]) => void;
  onPackageSearchChange?: (text: string) => void;
  materialNumberOptions: KPIFilterDropdownItem[];
  selectedMaterialNumbers: KPIFilterDropdownItem[];
  onMaterialNumberChange: (value: KPIFilterDropdownItem[]) => void;
  onMaterialNumberSearchChange?: (text: string) => void;
  materialOptions: KPIFilterDropdownItem[];
  selectedMaterials: KPIFilterDropdownItem[];
  onMaterialChange: (value: KPIFilterDropdownItem[]) => void;
  onMaterialSearchChange?: (text: string) => void;
  yearOptions: KPIFilterDropdownItem[];
  selectedYears: KPIFilterDropdownItem[];
  onYearChange: (value: KPIFilterDropdownItem[]) => void;
  periodOptions: KPIFilterDropdownItem[];
  selectedPeriods: KPIFilterDropdownItem[];
  onPeriodChange: (value: KPIFilterDropdownItem[]) => void;
  disabledPeriodIds: Set<string>;
  weekOptions: KPIFilterDropdownItem[];
  selectedWeeks: KPIFilterDropdownItem[];
  onWeekChange: (value: KPIFilterDropdownItem[]) => void;
  disabledWeekIds: Set<string>;
  onReset: () => void;
  onApply: () => void;
  isApplyEnabled: boolean;
  /** Filter keys that don't apply to the currently active tab — rendered read-only. */
  disabledFilterKeys?: Set<ExtendedFilterKey>;
  /** Tooltip text shown next to a disabled filter's label, explaining why it doesn't apply on this tab. */
  disabledFilterTooltip?: string;
  /** Quick-select time frame buttons (Week/Period/Quarter/Year-to-Date, Last Closed Week/Period). */
  activeQuickTimeFrame?: QuickTimeFrameKey | null;
  onQuickTimeFrameSelect?: (key: QuickTimeFrameKey) => void;
}

const QUICK_TIME_FRAME_OPTIONS: Array<{ key: QuickTimeFrameKey; label: string }> = [
  { key: 'wtd', label: 'Week-to-Date' },
  { key: 'ptd', label: 'Period-to-Date' },
  { key: 'qtd', label: 'Quarter-to-Date' },
  { key: 'ytd', label: 'Year-to-Date' },
  { key: 'lcw', label: 'Last Closed Week' },
  { key: 'lcp', label: 'Last Closed Period' },
];

const OrderRulesFilterBar: React.FC<OrderRulesFilterBarProps> = ({
  openFilterKey,
  setOpenFilterKey,
  regionOptions,
  selectedRegions,
  onRegionChange,
  marketOptions,
  selectedMarkets,
  onMarketChange,
  locationOptions,
  selectedLocations,
  onLocationChange,
  customerBpOptions,
  selectedCustomerBps,
  onCustomerBpChange,
  onCustomerBpSearchChange,
  customerOptions,
  selectedCustomers,
  onCustomerChange,
  onCustomerSearchChange,
  keyAccountOptions,
  selectedKeyAccounts,
  onKeyAccountChange,
  onKeyAccountSearchChange,
  packageOptions,
  selectedPackages,
  onPackageChange,
  onPackageSearchChange,
  materialNumberOptions,
  selectedMaterialNumbers,
  onMaterialNumberChange,
  onMaterialNumberSearchChange,
  materialOptions,
  selectedMaterials,
  onMaterialChange,
  onMaterialSearchChange,
  yearOptions,
  selectedYears,
  onYearChange,
  periodOptions,
  selectedPeriods,
  onPeriodChange,
  disabledPeriodIds,
  weekOptions,
  selectedWeeks,
  onWeekChange,
  disabledWeekIds,
  onReset,
  onApply,
  isApplyEnabled,
  disabledFilterKeys,
  disabledFilterTooltip,
  activeQuickTimeFrame,
  onQuickTimeFrameSelect,
}) => {
  const isFilterDisabled = (key: ExtendedFilterKey) => !!disabledFilterKeys?.has(key);

  const renderFilterLabel = (key: ExtendedFilterKey, label: string) => (
    <View style={styles.filterLabelRow}>
      <Text style={styles.filterLabel}>{label}</Text>
      {isFilterDisabled(key) && disabledFilterTooltip ? (
        <DLOTooltip content={disabledFilterTooltip} trigger="hover" position="top" maxWidth={280}>
          <View style={styles.orderRuleCheckInfoBtn}>
            <MaterialIcons name="info-outline" size={12} color="#9CA3AF" />
          </View>
        </DLOTooltip>
      ) : null}
    </View>
  );

  return (
    <View style={styles.filterBar}>
      {/* Main row: ~90% filters + ~10% actions */}
      <View style={styles.filterBarMainRow}>
        {/* Filters: two stacked rows */}
        <View style={styles.filtersTwoRowArea}>

          {/* Row 1: Region/Unit · Market/Pod · Location  |  Year · Period · Week */}
          <View style={styles.filtersRow}>
            <View style={[styles.filterGroup, styles.filterGroupThree]}>
              <View style={styles.filterItem}>
                {renderFilterLabel('region', 'Region / Unit')}
                <KPIFilterDropdown
                  childList={regionOptions}
                  selectedValue={selectedRegions}
                  setSelectedValue={onRegionChange}
                  placeholder="All"
                  isOpen={openFilterKey === 'region'}
                  onOpenChange={(open) => setOpenFilterKey(open ? 'region' : null)}
                  autoFocusSearchOnOpen
                  readOnly={isFilterDisabled('region')}
                />
              </View>

              <View style={styles.filterItem}>
                {renderFilterLabel('market', 'Market / Pod')}
                <KPIFilterDropdown
                  childList={marketOptions}
                  selectedValue={selectedMarkets}
                  setSelectedValue={onMarketChange}
                  placeholder="All"
                  isOpen={openFilterKey === 'market'}
                  onOpenChange={(open) => setOpenFilterKey(open ? 'market' : null)}
                  autoFocusSearchOnOpen
                  readOnly={isFilterDisabled('market')}
                />
              </View>

              <View style={styles.filterItem}>
                {renderFilterLabel('location', 'Location')}
                <KPIFilterDropdown
                  childList={locationOptions}
                  selectedValue={selectedLocations}
                  setSelectedValue={onLocationChange}
                  placeholder="All"
                  isOpen={openFilterKey === 'location'}
                  onOpenChange={(open) => setOpenFilterKey(open ? 'location' : null)}
                  autoFocusSearchOnOpen
                  sortItems
                  readOnly={isFilterDisabled('location')}
                />
              </View>
            </View>

            <View style={styles.filterGroupSeparator} />

            <View style={[styles.filterGroup, styles.filterGroupThree]}>
              <View style={styles.filterItem}>
                {renderFilterLabel('year', 'Year')}
                <KPIFilterDropdown
                  childList={yearOptions}
                  selectedValue={selectedYears}
                  setSelectedValue={onYearChange}
                  placeholder="All"
                  isOpen={openFilterKey === 'year'}
                  onOpenChange={(open) => setOpenFilterKey(open ? 'year' : null)}
                  autoFocusSearchOnOpen
                  readOnly={isFilterDisabled('year')}
                />
              </View>

              <View style={styles.filterItem}>
                {renderFilterLabel('period', 'Period')}
                <KPIFilterDropdown
                  childList={periodOptions}
                  selectedValue={selectedPeriods}
                  setSelectedValue={onPeriodChange}
                  placeholder="All"
                  isOpen={openFilterKey === 'period'}
                  onOpenChange={(open) => setOpenFilterKey(open ? 'period' : null)}
                  autoFocusSearchOnOpen
                  disabledIds={disabledPeriodIds}
                  readOnly={isFilterDisabled('period')}
                />
              </View>

              <View style={styles.filterItem}>
                {renderFilterLabel('week', 'Week')}
                <KPIFilterDropdown
                  childList={weekOptions}
                  selectedValue={selectedWeeks}
                  setSelectedValue={onWeekChange}
                  placeholder="All"
                  isOpen={openFilterKey === 'week'}
                  onOpenChange={(open) => setOpenFilterKey(open ? 'week' : null)}
                  autoFocusSearchOnOpen
                  disabledIds={disabledWeekIds}
                  readOnly={isFilterDisabled('week')}
                />
              </View>
            </View>
          </View>

          {/* Row 2: Customer BP · Customer · Key Account  |  Package · Material No. · Material */}
          <View style={styles.filtersRow}>
            <View style={[styles.filterGroup, styles.filterGroupThree]}>
              <View style={styles.filterItem}>
                {renderFilterLabel('customerBp', 'Customer BP')}
                <KPIFilterDropdown
                  childList={customerBpOptions}
                  selectedValue={selectedCustomerBps}
                  setSelectedValue={onCustomerBpChange}
                  placeholder="All"
                  isOpen={openFilterKey === 'customerBp'}
                  onOpenChange={(open) => setOpenFilterKey(open ? 'customerBp' : null)}
                  autoFocusSearchOnOpen
                  sortItems
                  readOnly={isFilterDisabled('customerBp')}
                  onSearchChange={onCustomerBpSearchChange}
                />
              </View>

              <View style={styles.filterItem}>
                {renderFilterLabel('customer', 'Customer')}
                <KPIFilterDropdown
                  childList={customerOptions}
                  selectedValue={selectedCustomers}
                  setSelectedValue={onCustomerChange}
                  placeholder="All"
                  isOpen={openFilterKey === 'customer'}
                  onOpenChange={(open) => setOpenFilterKey(open ? 'customer' : null)}
                  autoFocusSearchOnOpen
                  sortItems
                  readOnly={isFilterDisabled('customer')}
                  onSearchChange={onCustomerSearchChange}
                />
              </View>

              <View style={styles.filterItem}>
                {renderFilterLabel('keyAccount', 'Key Account')}
                <KPIFilterDropdown
                  childList={keyAccountOptions}
                  selectedValue={selectedKeyAccounts}
                  setSelectedValue={onKeyAccountChange}
                  placeholder="All"
                  isOpen={openFilterKey === 'keyAccount'}
                  onOpenChange={(open) => setOpenFilterKey(open ? 'keyAccount' : null)}
                  autoFocusSearchOnOpen
                  sortItems
                  readOnly={isFilterDisabled('keyAccount')}
                  onSearchChange={onKeyAccountSearchChange}
                />
              </View>
            </View>

            <View style={styles.filterGroupSeparator} />

            <View style={[styles.filterGroup, styles.filterGroupThree]}>
              <View style={styles.filterItem}>
                {renderFilterLabel('package', 'Package')}
                <KPIFilterDropdown
                  childList={packageOptions}
                  selectedValue={selectedPackages}
                  setSelectedValue={onPackageChange}
                  placeholder="All"
                  isOpen={openFilterKey === 'package'}
                  onOpenChange={(open) => setOpenFilterKey(open ? 'package' : null)}
                  autoFocusSearchOnOpen
                  sortItems
                  readOnly={isFilterDisabled('package')}
                  onSearchChange={onPackageSearchChange}
                />
              </View>

              <View style={styles.filterItem}>
                {renderFilterLabel('materialNumber', 'Material No.')}
                <KPIFilterDropdown
                  childList={materialNumberOptions}
                  selectedValue={selectedMaterialNumbers}
                  setSelectedValue={onMaterialNumberChange}
                  placeholder="All"
                  isOpen={openFilterKey === 'materialNumber'}
                  onOpenChange={(open) => setOpenFilterKey(open ? 'materialNumber' : null)}
                  autoFocusSearchOnOpen
                  sortItems
                  readOnly={isFilterDisabled('materialNumber')}
                  onSearchChange={onMaterialNumberSearchChange}
                />
              </View>

              <View style={styles.filterItem}>
                {renderFilterLabel('material', 'Material Description')}
                <KPIFilterDropdown
                  childList={materialOptions}
                  selectedValue={selectedMaterials}
                  setSelectedValue={onMaterialChange}
                  placeholder="All"
                  isOpen={openFilterKey === 'material'}
                  onOpenChange={(open) => setOpenFilterKey(open ? 'material' : null)}
                  autoFocusSearchOnOpen
                  sortItems
                  readOnly={isFilterDisabled('material')}
                  onSearchChange={onMaterialSearchChange}
                />
              </View>
            </View>
          </View>

        </View>

        {/* Actions column: ~10% width */}
        <View style={styles.filterActionsColumn}>
          <TouchableOpacity style={styles.resetBtn} onPress={onReset} testID="order-rules-reset-filters">
            <Text style={styles.resetBtnText}>Reset</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.applyBtn, isApplyEnabled ? styles.applyBtnEnabled : styles.applyBtnDisabled]}
            onPress={onApply}
            disabled={!isApplyEnabled}
            testID="order-rules-apply-filters"
          >
            <Text style={[styles.applyBtnText, isApplyEnabled && styles.applyBtnTextEnabled]}>Apply</Text>
          </TouchableOpacity>
        </View>
      </View>

      {onQuickTimeFrameSelect ? (
        <View style={styles.quickTimeFrameRow}>
          <Text style={styles.quickTimeFrameLabel}>Quick time frame</Text>
          {QUICK_TIME_FRAME_OPTIONS.map((option) => {
            const isActive = activeQuickTimeFrame === option.key;
            const isDisabled = isFilterDisabled('year');
            return (
              <TouchableOpacity
                key={option.key}
                style={[
                  styles.quickTimeFrameBtn,
                  isActive && styles.quickTimeFrameBtnActive,
                  isDisabled && styles.quickTimeFrameBtnDisabled,
                ]}
                onPress={isDisabled ? undefined : () => onQuickTimeFrameSelect(option.key)}
                disabled={isDisabled}
                activeOpacity={0.8}
                testID={`order-rules-quick-timeframe-${option.key}`}
              >
                <Text style={[styles.quickTimeFrameBtnText, isActive && styles.quickTimeFrameBtnTextActive]}>
                  {option.label}
                </Text>
              </TouchableOpacity>
            );
          })}
        </View>
      ) : null}
    </View>
  );
};

export default OrderRulesFilterBar;
